import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { fetchLinkStatus, switchSession } from '../../api/academyLink';

// "Entrar a mi Kitchen" / "Volver a mi tenant": the two directions of the same
// session handoff (auth-service /auth/academy-link/switch). The server swaps the
// refresh cookie; a full navigation then restarts every store under the new
// tenant, so nothing of the old tenant stays in memory.

const btn: React.CSSProperties = {
  height: 34, padding: '0 16px', borderRadius: 4, border: 'none', cursor: 'pointer',
  backgroundColor: 'var(--color-brand)', color: 'var(--color-on-brand)',
  fontSize: 13, fontWeight: 600,
};

/** Runs the handoff and navigates. Resolves false (and reports) on refusal. */
export function useHandoff(target: string) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const go = async () => {
    setBusy(true); setError('');
    try {
      await switchSession();
      window.location.assign(target);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setBusy(false);
    }
  };
  return { go, busy, error };
}

export const EnterKitchenButton: React.FC<{ target?: string; labelKey?: 'enroll.enter' | 'gate.enter' }> = ({
  target = '/academy', labelKey = 'gate.enter',
}) => {
  const { t } = useTranslation('academyLink');
  const { go, busy, error } = useHandoff(target);
  return (
    <div>
      <button type="button" onClick={go} disabled={busy} data-academy="enter-kitchen" style={{ ...btn, opacity: busy ? 0.7 : 1 }}>
        {busy ? t('gate.entering') : t(labelKey)}
      </button>
      {error && (
        <div role="alert" style={{ fontSize: 12, color: 'var(--color-error, var(--en-error))', marginTop: 8 }}>
          {t('gate.error', { detail: error })}
        </div>
      )}
    </div>
  );
};

/** The origin user's view of their enrollment: state plus the way in. Replaces
 *  the learner page in a tenant that is not the Kitchen (the writes live there). */
export const KitchenGate: React.FC<{
  tracks: { track_key: string; title: string; lessons_done: number; lessons_total: number }[];
}> = ({ tracks }) => {
  const { t } = useTranslation('academyLink');
  return (
    <div data-academy="kitchen-gate" style={{ maxWidth: 640, margin: '48px auto', padding: '0 16px' }}>
      <h1 style={{ fontSize: 20, fontWeight: 700, color: 'var(--color-text)', margin: '0 0 8px' }}>{t('gate.title')}</h1>
      <p style={{ fontSize: 13, lineHeight: 1.6, color: 'var(--color-text-subtle)', margin: '0 0 20px' }}>{t('gate.body')}</p>
      <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--color-text-muted)', marginBottom: 8 }}>
        {t('gate.tracks')}
      </div>
      <ul style={{ listStyle: 'none', margin: '0 0 24px', padding: 0, display: 'grid', gap: 8 }}>
        {tracks.map((tr) => (
          <li key={tr.track_key} style={{
            display: 'flex', justifyContent: 'space-between', gap: 12, padding: '10px 14px', borderRadius: 4,
            border: '1px solid var(--color-border)', backgroundColor: 'var(--color-surface, var(--color-base))',
            fontSize: 13, color: 'var(--color-text)',
          }}>
            <span>{tr.title}</span>
            <span style={{ color: 'var(--color-text-muted)' }}>
              {t('gate.progress', { done: tr.lessons_done, total: tr.lessons_total })}
            </span>
          </li>
        ))}
      </ul>
      <EnterKitchenButton labelKey="gate.enter" />
    </div>
  );
};

/** Slim strip for the pages that read from the Kitchen but cannot write from the
 *  origin tenant (the Maestro Pastelero stages page). Renders nothing otherwise. */
export const KitchenEntryBanner: React.FC = () => {
  const { t } = useTranslation('academyLink');
  const [origin, setOrigin] = useState(false);
  useEffect(() => {
    let live = true;
    fetchLinkStatus().then((s) => { if (live) setOrigin(s.linked && s.side === 'origin'); }).catch(() => undefined);
    return () => { live = false; };
  }, []);
  if (!origin) return null;
  return (
    <div data-academy="kitchen-banner" style={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap',
      padding: '10px 16px', borderBottom: '1px solid var(--color-border)',
      backgroundColor: 'var(--color-surface, var(--color-base))', fontSize: 13, color: 'var(--color-text-subtle)',
    }}>
      <span>{t('banner.text')}</span>
      <EnterKitchenButton target="/academy/master" labelKey="gate.enter" />
    </div>
  );
};
